import { lazy, Suspense } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'

import { ApiError } from '@/api/errors'
import { useCanAll, useLockedOut, useSessionStore, type Permission } from '@/auth'
import { isPlatformBrand, useBrandStore } from '@/brand'
import { ErrorState, Spinner } from '@/design'

const LandingPage = lazy(() => import('@/features/marketing/landing-page'))

/** Layout route: nothing renders until the refresh-cookie bootstrap has decided the session status. */
export function SessionGate() {
  const status = useSessionStore((state) => state.status)
  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <Spinner label="Loading your workspace" />
      </div>
    )
  }
  return <Outlet />
}

/**
 * Staff tree: needs a session; a locked-out user only ever sees /no-access.
 *
 * D14: on the PLATFORM host, a signed-out visitor at `/` sees the public marketing page instead
 * of being bounced to `/login` — the same path a signed-in user reaches the dashboard on, so no
 * app route moves. On a tenant's custom domain `/` still redirects to `/login`.
 */
export function RequireSession() {
  const status = useSessionStore((state) => state.status)
  const lockedOut = useLockedOut()
  const location = useLocation()
  const brand = useBrandStore((state) => state.brand)
  if (status !== 'authenticated') {
    if (location.pathname === '/' && isPlatformBrand(brand)) {
      return (
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center bg-paper">
              <Spinner />
            </div>
          }
        >
          <LandingPage />
        </Suspense>
      )
    }
    const returnTo = `${location.pathname}${location.search}`
    return (
      <Navigate
        to={returnTo === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(returnTo)}`}
        replace
      />
    )
  }
  if (lockedOut) return <Navigate to="/no-access" replace />
  return <Outlet />
}

/** Public tree: a signed-in user is sent home (or to /no-access). */
export function RequireAnonymous() {
  const status = useSessionStore((state) => state.status)
  const lockedOut = useLockedOut()
  if (status === 'authenticated') return <Navigate to={lockedOut ? '/no-access' : '/'} replace />
  return <Outlet />
}

/** /no-access is only for a locked-out session. */
export function RequireLockedOut() {
  const status = useSessionStore((state) => state.status)
  const lockedOut = useLockedOut()
  if (status !== 'authenticated') return <Navigate to="/login" replace />
  if (!lockedOut) return <Navigate to="/" replace />
  return <Outlet />
}

const forbidden = new ApiError({
  kind: 'api',
  status: 403,
  code: 'FORBIDDEN',
  message: 'Forbidden',
  details: { reason: 'role_lacks_permission' },
})

/** Renders the forbidden state in place — never a silent redirect (FRONTEND_PLAN §6.5). */
export function RequirePermission({
  requires,
  children,
}: {
  readonly requires: readonly Permission[]
  readonly children: React.ReactNode
}) {
  const allowed = useCanAll(requires)
  if (!allowed) return <ErrorState error={forbidden} />
  return children
}
